import React from "react";
import { ResponsiveContainer } from "recharts";

export const chartColorTokens = {
  primary: "#3551F3",
  primaryLight: "#3B82F6",
  success: "#22C55E",
  successDark: "#10B981",
  warning: "#EAB308",
  warningLight: "#F59E0B",
  orange: "#F97316",
  error: "#EF4444",
  errorDark: "#DC2626",
  info: "#06B6D4",
  textPrimary: "#0A0A0A",
  textSecondary: "#6B7280",
  textTertiary: "#737373",
  white: "#FFFFFF",
  borderPrimary: "#E5E5E5",
};

export const chartPalettes = {
  singleSeries: chartColorTokens.primary,
  dualAxis: [chartColorTokens.warning, chartColorTokens.successDark],
  investigation: chartColorTokens.primaryLight,
  regional: chartColorTokens.orange,
};

export const rechartsConfig = {
  common: {
    style: { fontFamily: "Inter, sans-serif" },
  },
  grid: {
    strokeDasharray: "3 3",
    stroke: chartColorTokens.borderPrimary,
    strokeOpacity: 0.8,
  },
  xAxis: {
    tick: {
      fill: chartColorTokens.textTertiary,
      fontSize: 12,
      fontFamily: "Inter, sans-serif",
    },
    axisLine: { stroke: chartColorTokens.borderPrimary },
    tickLine: { stroke: chartColorTokens.borderPrimary },
  },
  yAxis: {
    tick: {
      fill: chartColorTokens.textTertiary,
      fontSize: 12,
      fontFamily: "Inter, sans-serif",
    },
    axisLine: { stroke: chartColorTokens.borderPrimary },
    tickLine: { stroke: chartColorTokens.borderPrimary },
  },
  tooltip: {
    contentStyle: {
      backgroundColor: chartColorTokens.white,
      border: `1px solid ${chartColorTokens.borderPrimary}`,
      borderRadius: "8px",
      boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.1)",
      fontSize: "14px",
      fontFamily: "Inter, sans-serif",
    },
  },
  legend: {
    wrapperStyle: {
      fontSize: "12px",
      fontFamily: "Inter, sans-serif",
      color: chartColorTokens.textPrimary,
      paddingTop: "0px",
    },
  },
};

interface BaseChartProps {
  title: string;
  heightClassName?: string;
  headerRight?: React.ReactNode;
  contentMaxWidthPx?: number;
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  children?: React.ReactNode;
}

export const BaseChart: React.FC<BaseChartProps> = ({
  title,
  heightClassName = "h-80",
  headerRight,
  contentMaxWidthPx,
  isLoading = false,
  emptyState,
  children,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-5 border-b border-gray-200 flex items-center justify-between gap-3">
        <h3 className="font-semibold text-gray-800">{title}</h3>
        <div className="flex items-center gap-3">
          {isLoading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-gray-300 border-t-gray-600" />
              <span>Updating…</span>
            </div>
          ) : null}
          {headerRight}
        </div>
      </div>
      <div className="p-5">
        <div className={heightClassName}>
          {emptyState ? (
            emptyState
          ) : (
            <div
              className={
                contentMaxWidthPx
                  ? "relative h-full w-full mx-auto"
                  : "relative h-full w-full"
              }
              style={contentMaxWidthPx ? { maxWidth: contentMaxWidthPx } : undefined}
            >
              <ResponsiveContainer width="100%" height="100%">
                {children as any}
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
